import { api, ApiError } from "@/shared/api";
import { triggerDownload } from "@/shared/lib/download";
import { posterUploadSizeError } from "../model/posterUploadLimits";
import type { components } from "@/lib/api/generated";
import type { Override } from "@/shared/lib/typeOverride";
import type { Provider as MediaProvider, Tier as MediaTier } from "@/entities/media-provider/api";
import type { MediaTaskStatus } from "@/shared/lib/taskStatus";
// V6 fix4 (2026-05-12) — 新版 overlay (version/logo/qr bbox) 在 posterTemplateService 定义,
// PATCH /assets/:id/overlay + POST /assets/:id/finalize 用这个 shape;
// 老 PATCH /assets/:id/adjustment 仍用本文件下面的 legacy OverlayConfig。
import type { OverlayConfig as OverlayConfigV6 } from "./posterTemplateService";
// 配色 palette 的形状真源是长图那份(ai `longimage/style_tokens.py:StylePalette` 的对侧),
// 归整层 context 与它是同一份 —— 这里重抄一遍七个槽位必漂。
import type { StylePalette } from "../longimage/model/canvas";

// ===== Types =====
/**
 * 渲染模式 — Step5 三选一:
 * - SERVER_OVERLAY: AI 出底图 + 服务端精准叠加 logo/QR/contact(推荐,二维码可扫)
 * - LOGO_ONLY:     AI 出底图 + 只叠 logo,QR/contact 不叠(适合纯展示物料)
 * - AI_RENDER:     AI 直接画整张图,品牌信息融入画面(创意展示,二维码会扫不出)
 */
// 0926 第八批 Z3:generated.ts 改由 api 真 /v3/api-docs 生成后,后端这里是 String / Map,没有这个枚举/形状;
// 前后端约定的字面量留在前端这一处维护(旧 generated 里的同名定义来自手写 yaml)。
export type RenderMode = "SERVER_OVERLAY" | "LOGO_ONLY" | "AI_RENDER";
export type Provider = MediaProvider;
/**
 * 海报分辨率契约:画面比例 + 分辨率档位双维度。真实 W×H 在 ai-service 端
 * 按 (provider, ratio, tier) 三元组查 _tier_ratio_tables。
 */
export type PosterRatio = "1:1" | "9:16" | "16:9" | "3:4" | "4:5";
export type ResolutionTier = MediaTier;
// local-only: drift D-10 — frontend 6-state (PENDING/RUNNING/SUCCESS/PARTIAL/FAILED/CANCELLED) vs
// generated 4-state (PENDING/PROCESSING/COMPLETED/FAILED). V24 reconcile via
// 2026-05-17-fe-drift-poster-task-status.md proposal.
/**
 * 海报任务状态 = `media_task.status`,与视频/AI分身**同一列、同一套词汇**。
 *
 * 2026-07-16 收口到 MediaTaskStatus。此前这里是自己一份 6 态联合类型,**漏了 BLOCKED** ——
 * 后端真会产(安全拦截),TS 里却没有对应类型。
 */
export type TaskStatus = MediaTaskStatus;

// V11 (2026-05-12) — 加 FREEFORM_RAW: FreeCreate "真自由创作", ai-service 短路
// 走 rawPrompt 直传, 不再拼模板化骨架。
export type ContentSourceType = "KB_EXTRACT" | "FREEFORM" | "FREEFORM_RAW";

/** 后端 `content` 是 Map<String,Object> 原样落库,形状由前后端(ai ContentSource)约定,见 NOTE。 */
export interface ContentSourceFormBase {
  sourceType: ContentSourceType;
  kbItemId?: string | null;
  productName: string;
  sellingPoints?: string[];
  description?: string | null;
  /** sourceType=FREEFORM_RAW 时用 */
  rawPrompt?: string | null;
  scenario?: string | null;
  /** 用户补充(V26.5+ 改名为 user_override 含义) */
  cta?: string | null;
}

export type ContentSourceForm = ContentSourceFormBase & {
  /** handwritten override: sellingPoints required at runtime, selectedItems typed. */
  sellingPoints: string[];
  /**
   * s15 (2026-05-08): KB 多选时每个产品的结构化 bundle, 跟扁平的
   * productName/sellingPoints 同时存在。ai-service prompt_builder 在
   * selectedItems.length >= 2 时切到"按产品分组"渲染路径, 避免归属丢失。
   * FREEFORM 或单产品 KB 不发(undefined)。
   * mark: not yet in api-spec.yaml (V24 proposal)
   */
  selectedItems?: SelectedKbItem[];
  /**
   * 2026-09-01(爱民 hint 叠加):用户在文案框里**亲手打的字**,与拼装后的 rawPrompt
   * 并存。rawPrompt 是发给引擎的成串(原文 + 提交时追加的【画幅构图】【风格】等段),
   * 「用此提示词重新创作」若回填它,再提交就会把追加段再叠一层(774→804 那单,
   * 【画幅构图】出现两遍)。回填只能用这份原文。
   * backend `content` 是 Map<String,Object> 原样落库,ai 侧 ContentSource extra='ignore',
   * 两头都不用改契约。mark: not yet in api-spec.yaml。
   */
  userPrompt?: string;
};

/** s15: 提交给 backend / ai-service 的 KB 产品结构化 bundle, 字段与 KbExtractItem 一致但去掉前端展示用的额外属性。 */
// local-only: backend schema gap — generated ContentSourceForm.selectedItems is Record<string,never>[];
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface SelectedKbItem {
  itemId: string;
  name: string;
  summary: string;
  sellingPoints: string[];
}

/** 后端 `brand` 是 Map<String,Object>,形状由前端约定(见 NOTE)。 */
export interface BrandInfoFormBase {
  qrCodeMode?: "NONE" | "URL" | "UPLOAD";
  logoUploadId?: string | null;
  qrCodeUploadId?: string | null;
  qrCodeUrl?: string | null;
  phone?: string | null;
  email?: string | null;
  posterTitle?: string | null;
  posterSubtitle?: string | null;
  brandColors?: {
    primary?: string | null;
    secondary?: string | null;
    accent?: string | null;
  } | null;
}

export type BrandInfoForm = BrandInfoFormBase & {
  /** handwritten override: qrCodeMode required at runtime (generated marks optional). */
  qrCodeMode: "UPLOAD" | "URL" | "NONE";
};

/** S2 (2026-05-07): 9 宫格 anchor key 与 ai-service _AnchorKey 对齐。 */
// local-only: backend schema gap — used by LayoutOverridesForm send to backend;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export type AnchorKey9 =
  | "top_left"
  | "top_center"
  | "top_right"
  | "middle_left"
  | "middle_center"
  | "middle_right"
  | "bottom_left"
  | "bottom_center"
  | "bottom_right";

// local-only: backend schema gap — generated PosterTaskCreateRequest.layoutOverrides is
// Record<string,never>; V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md.
export interface LayoutOverridesForm {
  logoAnchor?: AnchorKey9;
  qrAnchor?: AnchorKey9;
  primaryHex?: string;
  secondaryHex?: string;
}

/** S2 (2026-05-07): wizard 砍 6→3 步,creative_overrides 简化只剩 extraConstraint(自然语言对话框)。 */
// local-only: backend schema gap — generated PosterTaskCreateRequest.creativeOverrides is
// Record<string,never>; V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md.
export interface CreativeOverridesPayload {
  extraConstraint?: string;
  /** 2026-07-13 — i2i 跟随原图:置 true 让 ai orchestrator 按参考图实测宽高定 ratio(前端量图失败的兜底)。 */
  ratioFollowsSource?: boolean;
}

// local-only: backend schema gap — generated PosterTaskCreateRequest stubs creative/layout/template/
// layoutOverrides/creativeOverrides as Record<string,never>; handwritten typed shape.
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface PosterTaskCreateRequest {
  /**
   * 批 L:确认卡上那张**正式报价单**的单号(`xb_price_quote.quote_id`)。
   *
   * ★ 后端按 `(tenant, quoteId)` 把单绑到 action_id,结算时按单兑现。
   *   **不带的话这一单会落一条无主的 MEDIA settle** —— 后端目前是软着陆(打
   *   `poster_task_without_quote` WARN 照常出图),前端全切完之后会改成硬拒。
   * ★ 显式声明在这里而不是靠对象展开塞进去:字段名打错时**没有任何东西会红** ——
   *   请求照发、后端照收、`quoteId` 恒 null、WARN 照打,而 WARN 没人天天看。
   *   (同族判据:`posterService.quote.test.ts` 断言它真的进了请求体。)
   */
  quoteId?: string;
  /** B6 场景卡打标(catalog sceneKey;缺省=不打标)。 */
  sceneKey?: string | null;
  /** S2 官媒配图归属(V18 归属列):reporter 稿 id + 槽位,两个必须同给(缺一后端 400)。 */
  reporterDraftId?: number;
  reporterSlot?: string;
  name?: string;
  /** S3 闭环:产出物归属的源任务 xb_task.id(执行人「去做」深链时带;空=自由创作)。 */
  sourceTaskId?: number;
  renderMode: RenderMode;
  provider: Provider;
  /**
   * Phase D (2026-05-16) — 画面比例,前端必填。
   * 替代旧的 `sizePreset`(已从 wire 删除)。
   * 真实 W×H 由 ai-service 按 (provider, ratio, resolutionTier) 三元组查表得出。
   */
  ratio: PosterRatio;
  /**
   * Phase D — 分辨率档位,前端必填。
   * 老路径允许空(后端走 provider defaultResolutionTier),新契约统一显式发,
   * 减少前后端默认值漂移。
   */
  resolutionTier: ResolutionTier;
  requestedCount?: number;
  content: ContentSourceForm;
  brand: BrandInfoForm;
  /** S2: 仅在用户在 Step2 9-grid 选了 logo/qr anchor 或调过主辅色才发。 */
  layoutOverrides?: LayoutOverridesForm;
  /** S2: 自然语言指令转 extraConstraint;空则不发。 */
  creativeOverrides?: CreativeOverridesPayload;
  /**
   * W6(2026-07-28)主体一致性锚定 —— 组图 / 多比例套图专用。
   *
   * 用户实测「出来的两只小猫看着也不一样」:组图每张爆点、套图每个比例都是独立 T2I,
   * 主体各画各的(连标题都会从「午后」漂成「午後」)。开启后 ai 侧第 1 张正常 T2I、
   * 后续张以第 1 张为 init image 走 image_edit。★代价是串行:3 张从约 1 分钟变约 3 分钟。
   * 单张 / 已挂参考图(那张本身就是锚)时后端自动忽略。
   */
  keepSubjectConsistent?: boolean;
  /**
   * 组图产品锚(2026-08-03 设计稿方案 A):产品实拍 adopt(from-product)后的 uploads
   * objectKey。backend 派发前兑换 presigned URL 改键 anchorImageUrl → ai 走 B2-1
   * 外部锚通路(逐张 GROUP 前缀 i2i,锁版面放取景)。仅组图态有意义,后端无台本时剥除。
   */
  groupAnchorRef?: string;
  /**
   * 组图主体锚句(2026-08-03 设计稿 B①):AI 排台本产出的产品外观锁定句,ai 侧
   * build_group_variants 逐张注入保主体一致(无实拍锚时的主体层)。
   */
  groupSubjectSpec?: string;
  /** 备注(=「特别要求」原文):落库 task.notes,供详情展示 + 重新创作回填;空则不发。 */
  notes?: string;
  /** v2.9.2 Step3 画面预设(本批所有图共享);detail 编辑器仍可单图覆盖。 */
  initialFilter?: FilterConfig;
  /**
   * V24 (2026-05-10) — 出图模式 t2i / i2i,仅套模板时有意义。
   * 默认 T2I (FreeCreate / 没选模板都走这条);I2I 只有模板有 cover_image_url 才能选。
   */
  mode?: "T2I" | "I2I";
  /**
   * 2026-05-25 — 自由创作 i2i(无模板):用户上传参考图的 objectKey(POST /poster/uploads 返回)。
   * 后端签成 presigned URL 喂 ai-service orchestrator image_edit 作 base image。
   */
  coverImageRef?: string;
  /** R5 局部重绘 mask 的 objectKey(白=要改;后端派发时与 coverImageRef 同规则 presign)。 */
  maskImageRef?: string;
  /**
   * W2-B9(2026-09-25)多图参考(模特试戴):≤4 张 {ref: objectKey, role}。ref 与 coverImageRef
   * 同规则(后端派发时 presign);coverImageRef 仍是底图,这里是参考,可共存。
   * ★只收 1K;不与组图 / 多比例套图 / 圈选组合(api 与 ai 都校)。不另计费。
   */
  refImageRefs?: Array<{ ref: string; role: "product" | "model" | "scene" | "style" }>;
  /** V24 — 模板的 templateId(数字 string,backend 查 V3 模板表注入 provider/prompt_t2i/cover/bbox)。 */
  template?: { templateId: string | number };
  /** S2 大师模式可选 wizard fields,backend 透传 ai-service。 */
  creative?: unknown;
  layout?: unknown;
  /** Wave 4.6 — 是否走"行业视觉提示"前置注入。 */
  useIndustryVisualHint?: boolean;
  /** Wave 4.6 — 仅 OPENAI provider,使用 GPT thinking 模式做 image-gen 决策。 */
  useGptThinking?: boolean;
  /** 2026-05-26 — 用户在自由创作选的风格名(StylePanel tone/自定义),详情页「本次设置」回显;模版生图不发。 */
  styleName?: string;

  // ── P-B (2026-07-20) 三模式 + 蓝图溯源(proposal 2026-07-20-poster-pb-group-multiratio-longimage)──
  /** 多比例套图:同内容一次出多比例(每比例 1 张)。与 groupPoints 互斥。 */
  ratios?: PosterRatio[];
  /**
   * B2-1 分步出图:`"cover"` 只出第 1 条(扣 1 张的钱),看过真图再走
   * `continueGroup` 出剩下;不传 = 一次全出(存量行为)。
   */
  groupStage?: "cover" | "all";
  /** 组图按爆点分张:每爆点独立 prompt;requestedCount 被忽略(张数=爆点数,≤6)。 */
  groupPoints?: Array<{ title: string; detail?: string }>;
  /** 蓝图溯源三件套:backend 建任务后自动 recordUsage(poster)(console 不调 /use 防双计)。 */
  blueprintId?: number;
  formulaName?: string;
  /** 蓝图 payload 透传(组图实例化用;backend 落库前白名单投影)。 */
  formulaPayload?: Record<string, unknown>;
  /** 详情长图整包(backend 分流 /longimage/orchestrate;结构见 LongImageCreatePayload)。 */
  longImage?: LongImageCreatePayload;
}

/** P-B 详情长图请求整包(ai 侧 pydantic 严校验;此处形状与 ai LongImageRequest 对齐)。 */
export interface LongImageCreatePayload {
  canvas: {
    direction: "v" | "h";
    width: number;
    scale: 1 | 2 | 3;
    background: "white" | "gray" | "brand";
    useCase: string;
    wideLayout?: "lr" | "tb" | "center" | "three";
    wideWidth?: number;
    wideHeight?: number;
    /** ★骨架稿(2026-08-05):ai 侧图块改画示意位,不下引擎不计费 */
    skeleton?: boolean;
  };
  style: { key: string; label: string; tone?: string };
  provider: Provider;
  /** ★用户意图单(copy/taboo,ADV-LI-COPY-TABOO-1)。**非空才发** ——
   *  无条件发会打破与 ai 侧 canvas_to_v1 的逐格对拍(那条 diff 是键集比对)。 */
  userIntent?: {
    copy?: Array<{ text: string; srcLine: number }>;
    taboo?: Array<{ text: string; srcLine: number }>;
  };
  blocks: Array<{
    id: string;
    primitive: "text" | "image" | "data";
    styleTag: string;
    name: string;
    props: Record<string, unknown>;
    /** D3 Canvas 双形态附加键:ai LongImageBlock extra=ignore 吞掉,backend 落 xb_detail_long_block。 */
    blockId?: string;
    order?: number;
    role?: string;
    renderMode?: string;
    layout?: string;
    content?: unknown;
    images?: unknown[];
    compliance?: unknown[];
    styleOverride?: unknown;
    /** ★原件槽(LI30-B,2026-09-10):原图直出块 —— 原样拼接,零引擎。 */
    originalKeys?: string[];
    originalFit?: "grid" | "full";
    screenAnno?: unknown;
  }>;
  blueprintId?: number;
  formulaName?: string;
  /** D2 统一视觉 DNA(双驱动 PIL 配色 + AI 图块 prompt;缺省=存量 background 推色)。 */
  styleTokens?: import("../longimage/model/canvas").StyleTokens;
  /**
   * LI-SCREEN-1 合屏 lane 标(2026-09-01):true = ai 按「屏」出图(相邻几段合成一屏
   * 一次画),一键成品「回搭建器改这一稿」后重新出图要带上 —— 不带会悄悄换轨成
   * 逐块渲染,观感与计费口径全变。api Map 透传;新建稿不发此键(逐块 lane 逐字不变)。
   */
  oneClick?: boolean;
}

/**
 * AI 模特换衣建单请求(2026-08-12)—— 对侧是 `POST /api/v1/poster/tryon/tasks`。
 *
 * 三个 objectKey 都走既有 `POST /poster/uploads`(kind=ref_image)拿,后端自己 presign
 * 喂上游;前端**不碰** presigned URL(页面 CSP 是 `connect-src 'self'`,前端 fetch 那张图
 * 在发出去之前就会被拒 —— 7.29 那句原生 `Failed to fetch` 就是这么来的)。
 */
// local-only: backend schema gap — /poster/tryon/tasks 尚未进 api-spec.yaml(新端点,与后端同批落地)。
export interface TryonTaskCreateRequest {
  /**
   * 模特底图 —— **一个动作一张**,顺序即出图顺序(seq 1..N)。
   *
   * 预置模特选「一组动作」时是 1~4 张;「用我自己的模特」那条入口长度恒为 1。
   * ★这是计费的自变量:N 张 = N 次上游调用 = N×500 积分,后端按 N 预检。
   */
  personObjectKeys: string[];
  /** 上装(**连衣裙也走这一位** —— 上游把连体装归在 top)。与下装至少给一件。 */
  topGarmentObjectKey?: string;
  /** 下装。 */
  bottomGarmentObjectKey?: string;
  /** true = 保留模特原脸;false = 生成新面孔(默认,合规更稳)。 */
  restoreFace: boolean;
  /** 任务打标(`TRYON_SCENE_KEY`);作品线场景筛按它筛。 */
  sceneKey: string;
  /**
   * 批 L 报价单号(修复波 0926 接上)。后端绑到 tryon 动作 id,结算按单兑现;
   * 不带 = 后端 `media_task_without_quote` 软着陆。显式声明,字段名打错 tsc 会红。
   */
  quoteId?: string;
}

/** S3 (2026-05-07 spec): 定时投放请求。后端 record 是 nested 结构 {scheduledAt, task}。 */
// local-only: backend schema gap — POST /poster/tasks/schedule body schema absent;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface PosterScheduleCreateRequest {
  scheduledAt: string;
  task: PosterTaskCreateRequest;
}

export type PosterScheduleDto = Override<
  components["schemas"]["PosterScheduleDto"],
  {
    /** handwritten runtime contract: these are non-null in steady state (generated marks nullable). */
    taskId: number | null;
    firedAt: string | null;
    failureReason: string | null;
  }
>;

// local-only: drift D-10 — handwritten uses local 6-state TaskStatus vs generated 4-state;
// generated PosterTaskDto schema otherwise complete. V24 reconcile via
// 2026-05-17-fe-drift-poster-task-status.md proposal.
export interface PosterTaskDto {
  /** B6 场景打标(历史任务 null)。 */
  sceneKey?: string | null;
  /** S2 官媒配图归属(V18 归属列;存量名约定任务为 null,前端按名兜底)。 */
  reporterDraftId?: number | null;
  reporterSlot?: string | null;
  id: number;
  name: string;
  status: TaskStatus;
  errorMessage: string | null;
  provider: Provider;
  renderMode: RenderMode;
  /** 画面比例(替代单维 sizePreset)。 */
  ratio?: PosterRatio;
  /** 分辨率档位(1K / 2K / 4K)。 */
  resolutionTier?: ResolutionTier;
  requestedCount: number;
  successCount: number;
  failedCount: number;
  submittedAt: string;
  completedAt: string | null;
  createdAt: string;
  // estimatedSecondsRemaining 已删(2026-07-16):后端 mapper 从建这个字段起就写死 null
  // ——「预计 N 秒」那行一次都没渲染过。api 侧 PosterTaskDto 同批删除;ai-service 不消费它
  // (已 grep 确认)。generated.ts 里那条来自 5 月的陈旧 api-spec,没人 import。
  /** v2.8 工作台元数据。tags 永远是数组(后端解析 JSON);isFavorite 永远是 boolean。 */
  tags: string[];
  isFavorite: boolean;
  notes: string | null;
  archivedAt: string | null;
  /** s15.6 (2026-05-08): 详情页 canvas 预览用 — 单端点 GET /tasks/{id} 注入这两个
   *  presigned URL (logoUploadId / qrCodeUploadId 解析签名), list 接口为 null. */
  logoUrl?: string | null;
  qrCodeUrl?: string | null;
  /**
   * 实交档(1006):api 列表按产物实交像素判出的档位(规则同 model/deliveredTier);老 api 不给 = 缺省。
   * 作品卡据此在实交低于所选时标实交档。
   */
  deliveredTier?: string | null;
  /** V22 (2026-05-15) — 创建者 user id + display_name。MANAGER+ 看全租户时显示"谁创建的"。
   *  STAFF 视图下 creatorUserId 就是自己,creatorUserName 用作展示但意义不大。 */
  creatorUserId?: number | null;
  creatorUserName?: string | null;
  /**
   * 2026-05-25 — 用户提交的原始文案/提示词(后端从 requestPayloadJson.content.rawPrompt 解析,
   * 已剥离 [AI-OPTIMIZED] sentinel)。详情页「本次设置」展示。仅 GET /tasks/{id} 注入,list 为 null。
   */
  rawPrompt?: string | null;
  /**
   * 2026-09-01 — 用户亲手打的原文(后端从 requestPayloadJson.content.userPrompt 解析,
   * 详情端点专属,list 为 null)。「用此提示词重新创作/出全渠道套图」回填用它;
   * 老单没存这份(null)→ 前端走 `recoverUserPrompt(rawPrompt)` 剥追加段兜底。
   */
  userPrompt?: string | null;
  /**
   * 2026-05-26 — 结果溯源(仅 GET /tasks/{id} 注入):templateId/templateName = 模版生图来源,
   * styleName = 自由创作选的风格。详情页「本次设置」展示模版名/风格 + 一键去模版生图。
   */
  templateId?: string | null;
  templateName?: string | null;
  styleName?: string | null;
  /**
   * 2026-05-30 — 列表封面缩略图 presigned URL。list 接口后端批量签一次(替代前端逐卡
   * listAssets 的 N+1),详情接口为 null(详情页有完整 asset 列表)。对齐 VideoTaskDto.coverThumbUrl。
   */
  coverThumbUrl?: string | null;
  /** P-B.1 三模式分流(typePayload.posterKind;老任务/普通海报 null)。 */
  /**
   * 产物形制。★"TRYON" 是 2026-08-12 加的换衣单 —— 后端一直在回它,
   * 前端类型漏登记会让 `task.posterKind === "TRYON"` 这类判断被 TS 判成「永不成立」而静默失效。
   */
  posterKind?: "LONG_IMAGE" | "GROUP" | "RATIO_SET" | "TRYON" | null;
  /**
   * 2026-07-28 详情回显两字段(**只有详情端点回**,list 恒 null —— backend 不逐行解 payload):
   *
   *   ratios            多比例套图的全部比例(如 ["9:16","1:1","16:9"])。
   *                     ★此前详情只有单值 `ratio`,套图任务在页面上只显示第一个 ——
   *                       出了三个比例却写着「9:16」,用户报的就是这条。
   *   groupPointTitles  组图爆点标题,顺序 = 产物 seq 顺序。没有它三张组图看上去
   *                     只是「三张不一样的图」,说不出哪张在讲哪个卖点。
   *
   * 两者皆可空:非套图 / 非组图 / 老任务都是 null,消费侧回落单值 `ratio`。
   */
  ratios?: string[] | null;
  groupPointTitles?: string[] | null;
  /**
   * 2026-07-30 W2 — 长图的**区块口径**计数(同样只有详情端点回;非长图恒 null)。
   *
   * `successCount/failedCount` 数的是「产物」,长图的产物只有整图一张 → 恒 1/0,页头据此
   * 写「已完成 · 1/1 成功」,而 8 个区块里挂了 3 个(实测 4717870055425)。长图的成败在**区块**上,
   * 页头/PARTIAL 横幅都改读这两个字段。
   */
  blockCount?: number | null;
  failedBlockCount?: number | null;
  /**
   * 2026-08-23 止血② 账本披露 —— 一键长图编排时 rejected 含**真料**(非大纲脚手架)
   * 的警示(ai 记账 → api 随 longImage 落 request_payload → 详情端点透出)。
   * 只在详情端点回;没警示 / 非长图 → null,前端不显示。不拦成功、只披露。
   */
  ledgerWarning?: {
    rejectedRealCount?: number;
    samples?: string[];
    message?: string;
  } | null;
  /**
   * 2026-09-11 图改图原图详情回显 —— i2i 参考原图的展示用 presigned URL(仅详情端点回,
   * 非 i2i / 解不开原图 → null;list 接口恒 null,同 logoUrl/qrCodeUrl 的口径)。
   */
  coverImageUrl?: string | null;
}

/**
 * Asset 字段对齐 backend Phase C / ai-service Phase A 双份上传链路。
 *
 *   字段语义:
 *     - baseUrl       4K PNG presigned URL(无 overlay,详情页 canvas + 下载源)
 *     - thumbUrl      1280 JPEG presigned URL(带 default overlay,grid 卡片预览)
 *     - actualWidth / actualHeight  真实出图像素
 *     - mime          固定 "image/png"
 *     - isUpscaled    GOOGLE Lanczos 上采样到 4K 时为 true,前端 grid badge "上采样 4K"
 *     - currentOverlayConfig / currentFilterConfig  当前生效的 overlay/filter 配置
 *     - originAssetId 版本链根 asset id
 *
 *   3 个 endpoint:
 *     - POST /assets/{id}/download         返 PNG attachment(实时合成下载)
 *     - POST /assets/{id}/preview-overlay  body {overlayConfig, filterConfig} 返 JPEG bytes(实时预览)
 *     - PUT  /assets/{id}/overlay          body {overlayConfig, filterConfig} 持久化 + 后端重 thumb
 */
export interface PosterAssetDto {
  id: number;
  taskId: number;
  seq: number;
  status: "PENDING" | "SUCCESS" | "FAILED";
  failureReason: string | null;
  /** 4K PNG presigned URL(详情页 canvas 显示 + 下载源)。 */
  baseUrl?: string | null;
  /** 1280 JPEG presigned URL(grid 卡片预览,体积小)。 */
  thumbUrl?: string | null;
  /** 实际像素宽高(backend 按 ratio×tier 三元组真实出图)。 */
  actualWidth?: number | null;
  actualHeight?: number | null;
  /** 通常 "image/png"。 */
  mime?: string | null;
  /** GOOGLE Lanczos 上采样到 4K 时 true;前端 grid badge "上采样 4K" 标识。 */
  isUpscaled?: boolean | null;
  /** 用户编辑后当前生效的 overlay/filter。 */
  currentOverlayConfig?: OverlayConfig | null;
  currentFilterConfig?: FilterConfig | null;
  /** 版本链根 asset id(版本链查询用)。 */
  originAssetId?: number | null;
  seed?: string | null;
  /** P-B.1 长图分段寻址(仅长图分段资产非空):assetMeta 透出。 */
  segment?: boolean | null;
  blockId?: string | null;
  /**
   * LI-SCREEN-1 合屏分段(2026-09-01):一键长图的分段=「屏」不=块。
   * screen=true 时 blockId 是屏 id(scr{n},刻意不与任何真块同名),blockIds 是这一屏
   * 装的编排块 id 清单 —— 搭建器成员块预览映射、分段面板屏卡都认它。
   * 形状不保证(后端从 assetMeta 原样透出),消费端自行收窄。
   */
  screen?: boolean | null;
  blockIds?: unknown;
  /** 生效渲染方式(A/B/C/S…),合屏段恒 "S"。 */
  renderMode?: string | null;
  /**
   * D4 合规降级命中([{raw,safe}, …];没命中恒 null)。
   *
   * 后端从 assetMeta 原样透出 —— 形状不保证(老数据/坏配置可能给非数组),
   * 消费端一律走 `complianceHits()` 收窄,不直接当数组用。
   */
  compliance?: unknown;
  blockName?: string | null;
  styleTag?: string | null;
  imagePromptPreview?: string | null;
  /**
   * 任务 22 / 2026-08-07 · 整图叠过品牌层(ai `_upload_all` 写 assetMeta.branded)。
   * 有它才说明「这张图上现在有 logo」—— 结果页的「摘掉」入口据此显隐。
   */
  branded?: boolean | null;
  /** v2.9 当前 asset 用的模版 id(detail 编辑器要据此渲染默认 anchors)。 */
  templateId?: string | null;
  /**
   * 1006 —— 画面调整的编辑底图(只对「加品牌」stamped 版本非空):回溯 origin 素图。
   * baseUrl 是已烙品牌的图,api 保存/预览重新合成用的是这张素图,编辑器预览必须同一张(否则旧 logo + 新 logo 成双)。
   */
  editBaseUrl?: string | null;
  /** 1006 —— 「加品牌」叠了什么(只对 stamped 版本非空),形状见 stampedOverlay.ts#StampedBrandInfo。 */
  stampedBrand?: unknown;
  /**
   * V6 fix4 (2026-05-12) — 用户点"完成下载"的时间戳;为空表示该 asset 还没被用户确认采纳。
   * 用作"已选用"标记,前端可在 grid 上加 badge 区分草稿 vs 已采纳。
   */
  selectedAt?: string | null;
}

// local-only: backend schema gap — generated KbExtractResponse.items is Record<string,never>[];
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface KbExtractItem {
  itemId: string;
  name: string;
  summary: string;
  sellingPoints: string[];
}

export type KbExtractResponse = Override<
  Omit<components["schemas"]["KbExtractResponse"], "items">,
  {
    /** handwritten override: typed items shape. */
    items: KbExtractItem[];
    // 0926 第八批 Z3:旧手写契约里可空、真 spec 不带可空信息的字段,在这里补回 | null
    runStatus: components["schemas"]["KbExtractResponse"]["runStatus"] | null;
  }
>;

/** 创作描述 → KB 命中预览。前端 600ms debounce 调用,展示 "已匹配 N 个相关产品" hint。 */
// local-only: backend schema gap — POST /poster/preview-kb-match response not in spec;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface PreviewKbMatchResponse {
  matchedCount: number;
  matchedItemNames: string[];
}

export type UploadResponse = components["schemas"]["UploadResponse"];

// 组图·AI 排台本(local-only:结构仅 ai 与 console 消费,backend Map 透传不强 schema)
export interface GroupScriptDraftPoint {
  title: string;
  detail?: string | null;
  /** 该条改写自输入的哪句话(逐字小段)—— coverage,「我们没编」要看得见 */
  source?: string | null;
}
export interface GroupScriptDraftResponse {
  points: GroupScriptDraftPoint[];
  subjectSpec: string;
  tone: string;
  /** true = 确定性兜底产物(卖点逐字/按句切),ai 侧已退票 */
  fallback: boolean;
}

// local-only: frontend query params builder — narrow union for status/bucket/mine,
// flattened into URLSearchParams in listTasks (no backend DTO needed).
export interface ListTasksParams {
  page?: number;
  size?: number;
  /** S2 官媒配图回捞:归属列优先+任务名兜底,租户级(带此参时其余筛选不参与)。 */
  reporterDraftId?: number;
  /** B6 场景筛(catalog sceneKey;缺省=不筛)。 */
  sceneKey?: string;
  /** 1102 只看收藏(true 才下推;缺省=不筛)。 */
  favorite?: boolean;
  /** 单个状态(后端只支持单值);多状态合并用 bucket。 */
  status?: TaskStatus;
  provider?: Provider;
  createdFrom?: string;
  createdTo?: string;
  /**
   * V11.2 (2026-05-12) — bucket 区分:
   *   "running" = PENDING/RUNNING(工作台进行中 section,不分页)
   *   "history" = SUCCESS/PARTIAL/FAILED/CANCELLED(工作台历史 section,真分页)
   *   undefined = 全部
   */
  bucket?: "running" | "history";
  /** V22 (2026-05-15) — MANAGER+ 切回"只看我的"视图;STAFF 传不传都强制只看自己。#9 起被 scope=mine 取代(保留兼容)。 */
  mine?: boolean;
  /**
   * #9 (2026-07-04) — 组织级归属筛选(替代 mine toggle)。all|department|member|mine;后端 Scopes 钳制在授权作用域内。
   * department 配 departmentId(子树),member 配 memberId(级联部门→成员)。
   */
  scope?: "all" | "department" | "member" | "mine";
  departmentId?: number;
  memberId?: number;
  /**
   * #7 (2026-07-04) — 名称搜索,下推后端做 name LIKE(全表搜)。原前端 .filter 只过滤当前页,
   * server 分页下会漏掉其它页命中项。空白串不下推(等于不过滤)。
   */
  q?: string;
}

// 后端统一用 common/PageResponse(records/total/page/size),跟其他模块(billing/tenants/
// customer-service)风格一致。原 PageResp(content/totalElements/number)是 Spring Data
// PageImpl 序列化形态,Spring 警告"JSON 结构不稳定",2026-05-07 切到 PageResponse。
// local-only: backend schema gap — common PageResponse<T> exists in backend but yaml's PageResponse
// schema is untyped (no records<T>); each list endpoint inlines records anonymously.
// V24 yaml align: introduce generic PageResponse via parameterized $ref pattern. See
// 2026-05-17-fe-generated-coverage-poster.md.
export interface PageResp<T> {
  records: T[];
  total: number;
  page: number;
  size: number;
}

// ===== Service =====
/**
 * 长图阶段一走 V2 分层链路的灰度开关(2026-08-11)。
 * true = /canvas/plan-v2;false = 一键退回 V1 /canvas/plan。
 * ★只影响端点选择,请求体/返回消费两端完全同形。
 */
export const LONGIMAGE_PLAN_USE_V2 = true;

/**
 * 上传前体积预检(0921 日志体检)。放在 service 这一层而不是各个页面里,是因为
 * `/poster/uploads` 有 6 个调用点(CanvasIntakeBox / BlockInspector ×2 / IntakeFollowupPanel /
 * FreeCreateBody / TryonCreatePage),逐个加必漏一个 —— 而漏掉的那个就是下一条 413。
 *
 * ★抛的是**后端自己那个码**(POSTER_UPLOAD_TOO_LARGE / 413),不是普通 Error:
 *   这样无论这一刀是前端提前落下的还是后端落下的,`posterErrorToast` 都映射成同一个
 *   「图片太大」标题,用户看到的东西完全一致 —— 界面不该因为拦截发生在哪一层而变脸。
 */
function assertPosterUploadSize(file: File): void {
  const msg = posterUploadSizeError(file);
  if (msg) throw new ApiError(msg, "POSTER_UPLOAD_TOO_LARGE", 413);
}

export const posterService = {
  getKbExtract: (opts?: { force?: boolean; tags?: string[] }) => {
    const params = new URLSearchParams();
    if (opts?.force) params.set("force", "true");
    for (const t of opts?.tags ?? []) params.append("tags", t);
    const qs = params.toString();
    return api.get<KbExtractResponse>(`/poster/kb-extract${qs ? `?${qs}` : ""}`);
  },

  /**
   * 创作描述 → KB 命中预览(后端 token 匹配,不调 ai-service)。
   * 前端 600ms debounce 后调用,展示 "已匹配 N 个相关产品" hint。
   */
  previewKbMatch: (description: string) =>
    api.post<PreviewKbMatchResponse>("/poster/preview-kb-match", { description }),

  uploadAsset: async (file: File, kind: "logo" | "qrcode"): Promise<UploadResponse> => {
    assertPosterUploadSize(file);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    return api.post<UploadResponse>("/poster/uploads", fd);
  },

  /**
   * 2026-05-25 — 自由创作图改图(i2i)参考图上传。复用 /poster/uploads(kind 仅做归类,
   * 后端按 tenantCode/{uuid}.{ext} 存,与 logo/qr 同链路);返回的 objectKey 作为
   * createTask 的 coverImageRef(后端解析为 presigned URL 喂 orchestrator image_edit)。
   */
  /** R5 局部重绘 mask 上传(与参考图同一个端点/桶;后端按 kind 区分用途)。 */
  uploadMaskImage: async (file: File): Promise<UploadResponse> => {
    assertPosterUploadSize(file);
    const fd = new FormData();
    fd.append("kind", "ref_image");
    fd.append("file", file);
    return api.post<UploadResponse>("/poster/uploads", fd);
  },

  /**
   * 把已有产物接成参考图 —— **后端在对象存储内部复制,浏览器不碰图片字节**
   * (2026-07-30,7.29 反馈任务 21 / 任务 9 根治)。
   *
   * ★老路是前端 `fetch(presignedUrl)` 下 4K 原图再上传,实测**必然失败**:页面 CSP 是
   * `connect-src 'self'`,请求在发出去之前就被浏览器拒了(同一个 URL `<img>` 能加载,
   * 因为 img-src 放行 https:;连 `mode:'no-cors'` 都抛 → 排除「只是缺 ACAO 头」)。
   * 用户看到的就是那句原生 `Failed to fetch`。跨域、超时、4K 图占浏览器内存一并消失。
   */
  adoptAssetAsRef: async (assetId: number): Promise<UploadResponse> =>
    api.post<UploadResponse>(`/poster/assets/${assetId}/adopt-as-ref`, {}),

  uploadRefImage: async (file: File): Promise<UploadResponse> => {
    assertPosterUploadSize(file);
    const fd = new FormData();
    fd.append("kind", "ref_image");
    fd.append("file", file);
    return api.post<UploadResponse>("/poster/uploads", fd);
  },

  /**
   * 2026-08-01 —— 把产品实拍图接成参考图(存储内复制,与 `adoptAssetAsRef` 同一条路)。
   *
   * ★**不要**在前端 presign 之后 fetch 那张图再上传:页面 CSP 是 `connect-src 'self'`,
   *   请求在发出去之前就被拒(7.29 那句原生 `Failed to fetch` 就是这么来的)。
   * ★只给 productCode + 第几张,key 由后端从产品树取。
   */
  adoptProductImageAsRef: (productCode: string, imageIndex = 0): Promise<UploadResponse> =>
    api.post<UploadResponse>("/poster/ref-images/from-product", { productCode, imageIndex }),

  /**
   * #13 i2i 重传:按 objectKey 重签参考图预览 URL(草稿恢复;原 24h presign 已过期)。
   * 越权防护(objectKey 须本租户 poster-uploads 前缀)在后端 service 层。
   */
  presignUpload: (objectKey: string): Promise<string> =>
    api.post<{ url: string }>("/poster/uploads/presign", { objectKey }).then((r) => r.url),

  createTask: (req: PosterTaskCreateRequest) => api.post<{ taskId: number }>("/poster/tasks", req),

  /**
   * AI 模特换衣建单(2026-08-12)—— 三步页提交口。
   *
   * ★与 `createTask` 分开是因为它**不是**一次 gpt-image-2 出图:引擎是百炼 `aitryon-plus`
   *   专项试衣模型,入参是三个 objectKey 而不是提示词/品牌信息那一整套表单。共用
   *   `PosterTaskCreateRequest` 只会让两边都长出一半用不上的可选字段。
   * ★产物仍落同一张 `media_task`(sceneKey=`ai_tryon`),所以详情页、轮询、作品线
   *   **一行都不用改** —— 拿到 taskId 直接跳 `/ai-haibao/tasks/:id`。
   */
  createTryonTask: (req: TryonTaskCreateRequest) =>
    api.post<{ taskId: number }>("/poster/tryon/tasks", req),

  /**
   * 换衣单张重出 —— 只重跑第 seq 个动作,只扣一张的钱,产物落回原来那一格。
   * ★重出失败时后端**不会**动原图(那张是用户已付费的成品),所以前端也不该先清空它。
   */
  regenerateTryonPose: (taskId: number, seq: number) =>
    api.post<void>(`/poster/tryon/tasks/${taskId}/poses/${seq}/regenerate`, {}),

  // 2026-07-30 — 删 `optimizePrompt`(legacy `/poster/optimize-prompt`)。
  // 它调用的是 wizard Step3 的「AI 整体优化」,而套模板路波A 已整条删除,这个函数
  // **全仓零调用方**。留着的代价不是几行字:它让人以为自由创作的「AI 优化」走的是这条,
  // 于是去配置中心调 `prompt_optimize` 那个 stage —— 调了不生效也说不出为什么。
  // 自由创作真正走的是下面的 `normalizeInput`(归整层)。

  /**
   * Wave 3 (2026-05-17): 自由创作 AI 优化按钮 — Qwen 归整 endpoint。
   * 三种 mode (full / minimal / titles_only),前端 AI 优化按钮用 full。
   * 替代旧 polishRawPrompt(已废弃)。
   */
  normalizeInput: (req: NormalizeInputRequest) =>
    api.post<NormalizeInputResponse>("/poster/normalize-input", req),

  /**
   * V76 (2026-05-28): 智能尺寸推断轻量端点。
   * smartActive(用户未手选 ratio)提交时调一次,ai-service 关键词命中走 fast path,
   * miss 调 LLM 5 选 1,失败兜底 4:5。永远返合法 ratio,不为 null。
   * 比 normalize-input 快(不跑 Qwen 全量归整),平均 < 1s。
   */
  inferRatio: (text: string) =>
    api.post<{
      ratio: "1:1" | "4:5" | "3:4" | "9:16" | "16:9";
      source: "keyword" | "llm" | "fallback";
    }>("/poster/infer-ratio", { text }),

  /**
   * V10 (2026-05-12): ApplyTemplate 套模板入口对核心卖点做 AI 润色.
   * 调 qwen 把 sellingPoints 数组重写成更有力的版本(数据/动作/结果),严格保持条数对齐,
   * 前端拿到后直接 setSellingPoints 回填到 input list,用户可继续手动微调.
   * 失败时 ai-service 已 fallback 原数组,这里只在网络层兜底.
   */
  polishSellingPoints: (req: { productName: string; sellingPoints: string[] }) =>
    api.post<{ polishedSellingPoints: string[] }>("/poster/polish-selling-points", req),

  /**
   * 任务 20「照这张再做一版」:参考图 objectKey → AI 看图写出的**创作说明**。
   *
   * ★不兜底:后端拿不到描述会抛(而不是返空串)。这条路的产物就是这段描述 ——
   *   兜一段空的等于让用户拿到一张与参考图毫无关系的图,而他以为系统看过那张图了。
   * ★传 objectKey 不传 URL:URL 由后端按租户前缀现签(防"给什么下什么")。
   */
  describeReference: (objectKey: string) =>
    api
      .post<{ description: string }>("/poster/describe-reference", { objectKey })
      .then((r) => r.description),

  /**
   * #15 改图指令优化:口语改图意图 → 清晰、可执行的机器改图指令(改图 tab「优化指令」按钮)。
   * 后端透传 ai;失败时 ai 侧已回退原文,前端只在网络层兜底。
   */
  optimizeEditInstruction: (instruction: string) =>
    api
      .post<{ optimizedInstruction: string }>("/poster/optimize-instruction", { instruction })
      .then((r) => r.optimizedInstruction),

  getTask: (id: number) => api.get<PosterTaskDto>(`/poster/tasks/${id}`),

  listTasks: (params: ListTasksParams = {}) => {
    const qs = new URLSearchParams();
    if (params.page !== undefined) qs.set("page", String(params.page));
    if (params.size !== undefined) qs.set("size", String(params.size));
    if (params.status) qs.set("status", params.status);
    if (params.provider) qs.set("provider", params.provider);
    if (params.bucket) qs.set("bucket", params.bucket);
    if (params.mine) qs.set("mine", "true");
    // #9 组织级筛选:scope=all 是默认(不下推,后端按角色作用域);其余下推 + 对应 id。
    if (params.scope && params.scope !== "all") qs.set("scope", params.scope);
    if (params.departmentId !== undefined) qs.set("departmentId", String(params.departmentId));
    if (params.memberId !== undefined) qs.set("memberId", String(params.memberId));
    if (params.q && params.q.trim()) qs.set("q", params.q.trim());
    // B6 场景筛下推(codex 修:此前只进了 queryKey,请求里没带 → 后端收不到筛条件)。
    if (params.sceneKey) qs.set("sceneKey", params.sceneKey);
    if (params.favorite) qs.set("favorite", "true");
    // S2 官媒配图回捞(归属列优先+名兜底;服务端两路 OR)
    if (params.reporterDraftId !== undefined) {
      qs.set("reporterDraftId", String(params.reporterDraftId));
    }
    return api.get<PageResp<PosterTaskDto>>(`/poster/tasks?${qs.toString()}`);
  },

  /**
   * 整体重试。修复波 0926(代理 H):body 从空对象变成可带报价单号 —— 后端
   * `@RequestBody(required=false) RetryRequest(quoteId)` 早就收,前端一直没发。
   * 调用方走 `model/retryQuote.retryPosterTask`(先取单再重试),别直接调这个裸口。
   */
  retryTask: (id: number, quoteId?: string | null) =>
    api.post<{ taskId: number }>(`/poster/tasks/${id}/retry`, quoteId ? { quoteId } : {}),

  /** D2 阶段一:上传图+产品信息 → 视觉理解 → Canvas 草稿(同步,分钟级;配额耗尽 429)。 */
  /**
   * 采集重构(2026-07-31)· 读懂一跳:大框原文 + 图 → 结构化事实 + 出处 + 缺口。
   *
   * 同步,带图时含 VLM(分钟级);429 = longimage_canvas 票配额耗尽。
   * ★返回体**必须**走 `parseComprehendResult` 验型,不要直接消费原始 JSON。
   */
  comprehendLongImage: (body: {
    rawText: string;
    images: Array<{ objectKey: string }>;
    product?: Record<string, unknown>;
    useCase?: string | null;
    targetWidth?: number;
    scale?: 1 | 2 | 3;
  }) => api.post<unknown>(`/poster/longimage/canvas/comprehend`, body),

  /**
   * 块级文案改写「换个说法」(2026-08-01)。
   *
   * ★ai 侧任何一环出问题都**退回原文** + `degraded=true`(合法结果,不是错误)——
   *   用户点的是一个润色按钮,它失败不该弹红色报错;但也不许假装改过了(所以有 reason)。
   */
  /**
   * 搭建器实时预览(2026-08-09):非图块确定性渲染,零计费。
   * ai 侧逐块成败:png(base64)或 error 二选一,一块坏不否决整包。
   */
  previewLongImageBands: (body: {
    canvas: { width: number; background?: string };
    styleTokens?: unknown;
    blocks: Array<{
      id: string;
      primitive: string;
      styleTag: string;
      name?: string;
      props: Record<string, unknown>;
      emphasis?: string;
    }>;
  }) =>
    api.post<{ bands: Array<{ id: string; png?: string; height?: number; error?: string }> }>(
      `/poster/longimage/preview-bands`,
      body
    ),

  refineLongImageCopy: (body: {
    text: string;
    rawBlob?: string;
    blockLabel?: string;
    intent?: string;
  }) =>
    api.post<{ text: string; degraded: boolean; reason: string }>(
      `/poster/longimage/copy/refine`,
      body
    ),

  /**
   * 补一段「让 AI 把这一段写出来」(2026-08-01)。
   *
   * ★数据段(规格/背书/价格/对照)ai 侧**直接拒** —— 没有真数据时编一句顶上比空着更糟。
   *   前端也不给那类段这个按钮,两道闸各司其职。
   */
  fillLongImageBlock: (body: {
    useCase: string;
    stepKey: string;
    product: Record<string, unknown>;
    existingTitles?: string[];
  }) =>
    api.post<{
      title: string;
      subtitle: string;
      bullets: string[];
      cta: string;
      degraded: boolean;
      reason: string;
    }>(`/poster/longimage/block/fill`, body),

  /**
   * 一段话 → 补齐画面窄槽(2026-08-05,D3)。
   *
   * ★ai 侧**只填空槽**:已经填过的一个字都不覆盖。补不出来退回原槽 + `degraded=true`
   *   (合法结果,不是错误)—— 用户点的是「让 AI 补细节」,补不出来不该弹红色报错;
   *   但也不许假装补过了,所以有 `filled` 逐格点名。
   */
  inferLongImageSlots: (body: {
    sentence: string;
    slots: Record<string, unknown>;
    rawBlob?: string;
    blockLabel?: string;
  }) =>
    api.post<{
      slots: Record<string, unknown>;
      filled: string[];
      degraded: boolean;
      reason: string;
    }>(`/poster/longimage/slots/infer`, body),

  /**
   * 阶段一 Canvas 规划 —— 端点由 {@link LONGIMAGE_PLAN_USE_V2} 灰度开关决定。
   *
   * ★V2(2026-08-11)= ai 侧分层链路(分流/语义抽取/图片理解/内容策划/视觉设计),
   *   请求/返回与 V1 **同形**,只多回 semantic / contentPlan 两个只读中间产物 ——
   *   现有 canvasBridge / 各测试零改动。线上出问题把开关翻回 false 即整链退回 V1,
   *   两端点共用同一张 longimage_canvas 票(计费不变)。
   */
  planLongImageCanvas: (body: import("../longimage/model/canvas").CanvasPlanRequest) =>
    api.post<import("../longimage/model/canvas").LongImageCanvas>(
      LONGIMAGE_PLAN_USE_V2 ? `/poster/longimage/canvas/plan-v2` : `/poster/longimage/canvas/plan`,
      body
    ),

  /**
   * P2 异步 plan(2026-08-17)· 提交=入队:body = CanvasPlanRequest + draftId(console 先 ensure 草稿)。
   * 立即返作业(QUEUED),page 轮询 {@link getLongImagePlanJob};离开无损、回来接恢复横幅「编排已完成」。
   */
  submitLongImagePlanAsync: (
    body: import("../longimage/model/canvas").CanvasPlanRequest & { draftId: number }
  ) =>
    api.post<import("../longimage/model/canvas").LongImagePlanJob>(
      `/poster/longimage/canvas/plan-async`,
      body
    ),

  /**
   * 一键出图(直达模式,2026-08-20)提交 = 入队:body = CanvasPlanRequest + draftId + styleTone。
   * 与 {@link submitLongImagePlanAsync} 同一形状、同一轮询端点,差别在 worker 侧
   * (多跑读懂段、产物全 A、DONE 后 backend 接续自动出图)。
   */
  submitLongImageOneClick: (
    body: import("../longimage/model/canvas").CanvasPlanRequest & {
      draftId: number;
      styleTone?: string;
      /** ★品牌二维码(2026-08-24 P1「一键路 QR 上传槽」):备料页从品牌资产库选的 QR,
       *  产物在整图品牌层确定性贴上。UPLOAD→qrCodeUploadId(objectKey),URL→qrCodeUrl。
       *  后端在一键接续(startGeneration)落进 longImage.brand,ai 侧无改动即消费。 */
      qrCodeMode?: "UPLOAD" | "URL";
      qrCodeUploadId?: string;
      qrCodeUrl?: string;
    }
  ) =>
    api.post<
      import("../longimage/model/canvas").LongImagePlanJob & { posterTaskId?: number | null }
    >(`/poster/longimage/oneclick`, body),

  /** 轮询一键作业;DONE 时带 posterTaskId(接续建的海报任务,console 据它跳成品页)。 */
  getLongImageOneClickJob: (jobId: number) =>
    api.get<
      import("../longimage/model/canvas").LongImagePlanJob & {
        posterTaskId?: number | null;
        /**
         * FAILED 但出图**可能仍在跑、会扣钱**(海报任务已建、只是任务号没写回;api Z2 0926)。
         * true 时前端不许说「没扣积分」,改指去「我的作品」。
         */
        mayBeGenerating?: boolean;
      }
    >(`/poster/longimage/plan-jobs/${jobId}`),

  /** P2 轮询长图 plan 作业状态(QUEUED/RUNNING/DONE/FAILED;canvas 产物走 getDraft 的 planResultJson)。 */
  getLongImagePlanJob: (jobId: number) =>
    api.get<import("../longimage/model/canvas").LongImagePlanJob>(
      `/poster/longimage/plan-jobs/${jobId}`
    ),

  /**
   * 一键出图最近作业列表(owner-scoped,只读,2026-09-04「点完即走」)。
   *
   * ★存在的理由:一键出图提交那一刻拿到的 jobId 只活在那次页面渲染里 —— 切页面/关页面就丢,
   *   而「读懂+排区块+派发」这一段在此之前**没有任何 xb_media_task 行**(AFTER_COMMIT 才建),
   *   draftMeta.oneclick 也只在作业**终态**才写一格,在途窗口完全不可见。这扇门直接查作业表,
   *   `useCanvasBuilder` 恢复扫描据它认出「回来时还在跑/已经判失败」的单,重接轮询或把护栏
   *   reason 原样带回来;`pages/works` 的「进行中」节也据它给这段窗口画一张卡(否则用户点完
   *   一键、切到作品页,这段时间那单彻底不存在)。
   *
   * 最近 20 条,不按 draftId/status 过滤(前端自己挑)。
   */
  listLongImageOneClickJobs: () =>
    api.get<
      Array<
        import("../longimage/model/canvas").LongImagePlanJob & {
          posterTaskId?: number | null;
          createdAt?: string | null;
          /** 与 {@link getLongImageOneClickJob} 同一 DTO(MediaDraftJobDto):FAILED 但出图可能仍在跑。缺省=没标。 */
          mayBeGenerating?: boolean;
        }
      >
    >(`/poster/longimage/oneclick/jobs`),

  /** R4 横宽轻规划:产品资料 → 推荐版式 + 各分区内容(同步;配额耗尽 429)。返回体走 parseWidePlan 验型。 */
  planLongImageWide: (body: {
    wideLayout?: string | null;
    placement?: string;
    product: { name: string; sellingPoints: string[]; specs?: string; brand?: string };
    notes?: string;
  }) => api.post<unknown>(`/poster/longimage/wide/plan`, body),

  /**
   * P-B.1 长图单块重生成:只重出该图块(一笔出图计费)+ 重 stitch;轮询 segSeq 分段 PENDING→终态。
   *
   * ★`opts` 是块级编辑器的「AI 改图 / AI 重画」(2026-08-08):
   *   `mode: "edit"` 保留构图改局部(i2i,后端拿该块上一版的 **base** 当锚)/ `"semantic"` 推翻重画。
   *   不给 = 老行为(按 props 原样重出),块行那颗「重出」照旧。
   *
   * ★分段**不能**走海报的 `regenerateAsset`:那条对长图任务是穿得进去的(线别与 kind 全一样),
   *   穿进去会把整个长图任务打回 PENDING 并派海报编排盖掉那一段且永不重拼。后端已显式拒,
   *   前端也别去试 —— 长图的重出只有这一条路。
   */
  regenerateLongImageBlock: (
    taskId: number,
    blockId: string,
    // promptOverride(屏卡 P1,0910):用户在重画弹层里编辑过的整段提示词,
    // ai 合屏 lane 逐字使用(instruction 是「一句修改要求」的轻量路,两者互斥语义)。
    opts?: { instruction?: string; mode?: "edit" | "semantic"; promptOverride?: string }
  ) =>
    api.post<{ taskId: number; blockId: string; regenId: string; segSeq: number }>(
      `/poster/tasks/${taskId}/longimage/blocks/${encodeURIComponent(blockId)}/regenerate`,
      opts ?? {}
    ),

  /**
   * 这一稿**实际发给引擎的那份内容**(2026-08-08):「回搭建器改这一稿」的回填源。
   *
   * ★不能靠搭建器自己的草稿:那份是**按人存一份**的(`useCanvasBuilder` 拉 listDrafts 取第一份
   *   能解析的,与 taskId 无关)—— 用户出过图 A、又新做了一稿 B,再从 A 点回搭建器,
   *   躺在那儿的是 B。从任务反解才是这张图的生成真相。
   */
  readLongImageSpec: (taskId: number) =>
    api.get<Record<string, unknown>>(`/poster/tasks/${taskId}/longimage`),
  /**
   * 任务 16 · 长图删块 / 撤销删块 → 纯重拼(**零出图、零出图积分**)。
   *
   * ★后端实现成打 `hidden` 标记而不是真删:分段 seq 契约是「块序 index+2」,真摘掉一块
   *   会让后面每块的 seq 全部前移、而它们的字节一个都没变。打标记 → 只有整图重传一张,
   *   撤销就是把标记翻回去(所以撤销是白拿的,不是我们额外做的补偿逻辑)。
   */
  /**
   * 设计锚点 ⑤ · 出图**之后**改了这一块的内容 → 落库 + 按块类型分两档派发(2026-08-01)。
   *
   * ★`tier` 由**后端**裁决并回给前端(paid=图块重画按单块计一笔 / free=只重拼零出图),
   *   前端只负责把它说出来。两边各判各的迟早出现「界面说不花钱、实际扣了」。
   */
  applyLongImageBlockEdit: (
    taskId: number,
    blockId: string,
    props: Record<string, unknown>,
    /** v2 结构化 content —— 与 props 一起换,否则分段面板上会留着旧标题。 */
    content?: Record<string, unknown>
  ) =>
    api.post<{ taskId: number; blockId: string; tier: "paid" | "free" }>(
      `/poster/tasks/${taskId}/longimage/blocks/${encodeURIComponent(blockId)}/apply-edit`,
      { props, ...(content ? { content } : {}) }
    ),

  setLongImageBlockHidden: (taskId: number, blockId: string, hidden: boolean) =>
    api.post<{ taskId: number; blockId: string; hidden: boolean; restitchId: string }>(
      `/poster/tasks/${taskId}/longimage/blocks/${encodeURIComponent(blockId)}/hidden`,
      { hidden }
    ),

  /**
   * 2026-08-07 · 出图**之后**给这张长图挂 logo / 二维码 —— 走删块那条纯重拼:
   * 分段字节一个不动 → **零出图、零计费**,位置可以反复改。
   *
   * ★形状复用海报那套 `BrandInfo` / `LayoutOverrides`(ai 侧注释明写「不另造一套 ——
   *   另造等于给同一件事立第二套契约,以后必漂」)。
   * ★`brand` 传 null / 空对象 = **摘掉**品牌层(重拼回没有 logo 的那版),不是「这次不改」。
   */
  /**
   * 2026-08-08 · 拿既有分段重拼一次(零出图、零计费)。
   *
   * ★块级调整保存之后必须调它:`updateAssetOverlay` 只更新了**那一段**的成品字节
   * (`assetMeta.finalKey`),整图是拼出来的 —— 不再拼一次,用户看到的还是改之前那张。
   */
  restitchLongImage: (taskId: number) =>
    api.post<{ taskId: number; restitchId: string }>(
      `/poster/tasks/${taskId}/longimage/restitch`,
      {}
    ),

  setLongImageBrand: (
    taskId: number,
    body: {
      brand?: {
        logoUploadId?: string;
        qrCodeMode?: "NONE" | "UPLOAD" | "URL";
        qrCodeUploadId?: string;
        qrCodeUrl?: string;
      } | null;
      layoutOverrides?: { logoAnchor?: string; qrAnchor?: string } | null;
    }
  ) =>
    api.post<{ taskId: number; restitchId: string; branded: boolean }>(
      `/poster/tasks/${taskId}/longimage/brand`,
      body
    ),

  /** 取消在途任务(PENDING/RUNNING → CANCELLED;终态拒)。2026-06-12 遗留拍板:后端端点早有,UI 入口缺。 */
  cancelTask: (id: number) => api.post<void>(`/poster/tasks/${id}/cancel`, {}),

  /**
   * 组图·AI 排台本(2026-08-03 设计稿 A):产品/用户文案 → 逐张台本 + 主体锚句 + 基调句。
   * 免费:ai 侧开票 action_type=poster_text_assist,价表 pricing_mode=FREE,结算实扣 0
   *(旧注释里的 55~70 档是 07-31 的实测值,早已过时);fallback=true 表示确定性兜底产物。
   */
  draftGroupScript: (body: {
    product?: { name: string; sellingPoints: string[]; specs?: string[]; summary?: string };
    userText?: string;
    brief?: string;
    maxPoints?: number;
    /** 用户选的整组画风(画面设计与其协调;「自由发挥」不传)。 */
    styleHint?: string;
  }): Promise<GroupScriptDraftResponse> =>
    api.post<GroupScriptDraftResponse>("/poster/group/draft-script", body),

  deleteTask: (id: number) => api.delete<void>(`/poster/tasks/${id}`),

  listAssets: (taskId: number) => api.get<PosterAssetDto[]>(`/poster/tasks/${taskId}/assets`),

  // ===== v2.8 工作台:草稿 / 元数据 / 单图重生 / 派生 =====

  listDrafts: () => api.get<PosterDraftDto[]>("/poster/drafts"),

  getDraft: (id: number) => api.get<PosterDraftDto>(`/poster/drafts/${id}`),

  upsertDraft: (req: UpsertDraftRequest) => api.post<PosterDraftDto>("/poster/drafts", req),

  deleteDraft: (id: number) => api.delete<void>(`/poster/drafts/${id}`),

  updateTaskMeta: (id: number, req: UpdateTaskMetaRequest) =>
    api.patch<PosterTaskDto>(`/poster/tasks/${id}`, req),

  /**
   * v2.8 单图重生 / v2.9 Semantic 重画。扣 1 张图 quota + (可选)1 次 prompt_refine LLM token。
   *
   * - body 全空 → 同 prompt 不同 seed(v2.8 行为)
   * - body.presetId → 后端从 instruction_presets.yaml 取 instruction
   * - body.instruction → 用户自定义文本(presetId 优先)
   */
  regenerateAsset: (
    taskId: number,
    assetId: number,
    body?: {
      presetId?: string;
      instruction?: string;
      preserveOverlay?: boolean;
      replace?: boolean;
      /** S2 (2026-05-07): "edit" 走 image_edit i2i (保主体);"semantic" 走完全新 prompt 重画。 */
      mode?: "edit" | "semantic";
      /**
       * 批 L:正式报价单号。**单图重生是真出一张图、真扣一笔钱**,所以它和建单一样要有自己的单。
       * 后端绑到 `{taskId}:regen:{assetId}:{seed}` 这个 action_id 上。
       */
      quoteId?: string;
    }
  ) => api.post<void>(`/poster/tasks/${taskId}/assets/${assetId}/regenerate`, body ?? {}),

  /**
   * B2-1 组图续出:照着封面出剩下几张。
   *
   * ★不带 body —— 台本(完整 groupPoints)取后端库里存的那份。前端重传一份就会出现
   *   「前端记的台本 ≠ 后端存的」,而两边不一致时用户是按前端看到的付钱。
   */
  continueGroup: (taskId: number, quoteId?: string | null) =>
    api.post<void>(
      `/poster/tasks/${taskId}/group/continue`,
      // 批 L:body 从空对象变成可带单号。后端 `@RequestBody(required=false)`,老前端照旧能调。
      quoteId ? { quoteId } : {}
    ),

  /**
   * B2-3 组图单张重出:改这条爆点的文案、只重画这一张(★后端锚封面,不是锚这张自己)。
   *
   * ★`detail` 不传 = **不动原来的画面提示**;传空串才是主动清空 —— 用户只改标题时
   *   不能把他之前写的画面提示静默弄丢。
   */
  redrawGroupImage: (
    taskId: number,
    seq: number,
    title: string,
    detail?: string,
    quoteId?: string | null
  ) =>
    api.post<void>(`/poster/tasks/${taskId}/group/${seq}/redraw`, {
      title,
      detail,
      ...(quoteId ? { quoteId } : {}),
    }),

  /** S3 sprint 真实接入;S2 client 已声明,UI 在 Step3 排程 radio 调用。 */
  createSchedule: (req: PosterScheduleCreateRequest) =>
    api.post<{ scheduleId: number }>("/poster/tasks/schedule", req),

  listSchedules: () => api.get<PosterScheduleDto[]>("/poster/tasks/schedule"),

  cancelSchedule: (id: number) => api.delete<void>(`/poster/tasks/schedule/${id}`),

  // ===== v2.9 模版库 / Semantic 重画 chip 库(backend 透传 ai-service yaml) =====

  listTemplates: () => api.get<TemplatesPayload>("/poster/templates"),

  listInstructionPresets: () => api.get<PresetsPayload>("/poster/instruction-presets"),

  /** v2.9 当前租户的 ai_poster runtime 参数 (Step6 数量 selector 用)。 */
  getMeConfig: () =>
    api.get<{ defaultCount: number; maxCount: number; minCount: number }>("/poster/me-config"),

  /** V26 (2026-05-14) Wave 4.3 — 当前租户海报上下文(industryCode + 中文 industryName)。 */
  getPosterMeContext: () =>
    api.get<{ industryCode: string; industryName: string }>("/poster/dict/me-context"),

  /**
   * v2.9 详情页 Layout / Filter 调整持久化(不扣费)。
   * 请求体任一字段省略则不变。
   */
  updateAssetAdjustment: (
    taskId: number,
    assetId: number,
    body: { overlay?: OverlayConfig | null; filter?: FilterConfig | null }
  ) => api.patch<PosterAssetDto>(`/poster/tasks/${taskId}/assets/${assetId}/adjustment`, body),

  /**
   * V6 fix4 (2026-05-12) — 用户拖拽编辑页保存 logo/QR 位置 → 同步 recompose, 返回新 fullImageUrl。
   * 服务端同步 ~2-3s (ai-service compose + MinIO put + DB update);409 表示同 asset 正在合成,稍后重试。
   */
  updateAssetOverlay: (
    taskId: number,
    assetId: number,
    overlay: OverlayConfigV6,
    filter?: FilterConfig | null
  ) =>
    api.patch<PosterAssetDto>(`/poster/tasks/${taskId}/assets/${assetId}/overlay`, {
      overlay,
      ...(filter !== undefined && filter !== null ? { filter } : {}),
    }),

  /**
   * V6 fix4 (2026-05-12) — 用户点"完成下载"按钮:标记 selectedAt(幂等) + 触发审计。
   * 返回的 downloadUrl 跟 listAssets 一致,带 attachment 强制下载。
   */
  finalizeAsset: (taskId: number, assetId: number) =>
    api.post<PosterAssetDto>(`/poster/tasks/${taskId}/assets/${assetId}/finalize`, {}),

  // ===== Phase D (2026-05-16) — Asset 下载 / 实时预览 / overlay 持久化新契约 =====

  /**
   * 下载产物(清库重建 cutover)— presign-URL 模型:backend 选 finalKey(有 overlay 成品)
   * 否则原图,MinIO 签 `response-content-disposition: attachment; filename` 返 `{url}`。
   * 前端直接 `triggerDownload(url, filename)`(跨 origin `<a download>` 被忽略,靠 MinIO
   * 吐的 Content-Disposition 头下载)。回到 Phase D 前的 presigned-URL 范式。
   */
  downloadAsset: async (assetId: number): Promise<string> => {
    const res = await api.post<{ url: string }>(`/poster/assets/${assetId}/download`, {});
    return res.url;
  },

  /**
   * #26 多选批量下载:所选任务的全部成品图后端打成一个 zip 流式返回,前端拿 blob 触发下载。
   * owner 级归属校验在后端 service 层(STAFF 越权下他人产物防护)。filename 空则用默认包名。
   */
  batchDownload: async (taskIds: number[], filename?: string): Promise<void> => {
    const body = filename?.trim() ? { taskIds, filename } : { taskIds };
    const res = await api.sendBlob("POST", "/poster/assets/batch-download", body);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const trimmed = filename?.trim();
    const zipName = trimmed
      ? trimmed.toLowerCase().endsWith(".zip")
        ? trimmed
        : `${trimmed}.zip`
      : "海报批量下载.zip";
    try {
      triggerDownload(url, zipName);
    } finally {
      // 延迟 revoke:同步 <a>.click 触发下载后再释放,避免浏览器还没读就失效。
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  },

  /**
   * Phase D — 编辑器实时预览(debounce 300ms 调用)。
   * 发当前 overlay + filter,backend 拿 base + 临时合成返 JPEG bytes(小体积、快)。
   * 不写盘、不扣费,纯预览。
   */
  previewAssetOverlay: async (
    assetId: number,
    body: {
      overlayConfig?: OverlayConfig | OverlayConfigV6 | null;
      filterConfig?: FilterConfig | null;
    }
  ): Promise<Blob> => {
    const res = await api.sendBlob("POST", `/poster/assets/${assetId}/preview-overlay`, body);
    return res.blob();
  },

  /**
   * Phase D — 用户点"保存"持久化当前 overlay + filter。
   * backend 走 mq:落 asset.current_overlay_config / current_filter_config + 重生 thumb,
   * 返 200 no body。前端 onSuccess 重拉 task 拿最新 thumbUrl。
   */
  saveAssetOverlay: (
    assetId: number,
    body: {
      overlayConfig?: OverlayConfig | OverlayConfigV6 | null;
      filterConfig?: FilterConfig | null;
    }
  ) => api.put<void>(`/poster/assets/${assetId}/overlay`, body),

  /**
   * 0713 批量品牌变体(一行一张图):每行点名 logo/二维码(id 或名字,行内至少一个),
   * 全行共用 overlayConfig 锚点(null=模板默认)。逐行确定性叠合产新版本,不扣费。
   * 部分失败不整单回滚:created 已落版本,failed 带行名+原因。上限 10 行,同组合去重。
   * local-only 契约,proposal: docs/contracts/v23/proposals/2026-07-13-poster-brand-variants.md
   */
  generateBrandVariants: (
    taskId: number,
    assetId: number,
    body: {
      overlayConfig?: Record<string, unknown> | null;
      variants: Array<{ name?: string; logoRef?: string; qrRef?: string }>;
    }
  ) =>
    api.post<{
      created: PosterAssetDto[];
      failed: Array<{ name?: string | null; message: string }>;
    }>(`/poster/tasks/${taskId}/assets/${assetId}/variants`, body),
};

// ===== v2.8 DTOs =====
export type PosterDraftDto = Override<
  components["schemas"]["PosterDraftDto"],
  {
    /** handwritten runtime contract: updatedAt always present (generated marks optional). */
    updatedAt: string;
    /**
     * 一键出图作业态(done|failed|generating|null,从 draftMeta.oneclick 解)。
     *
     * ★存在的理由是**堵一个 C-2 自己造出来的回归**(LI-DONE-NOPIC,2026-08-22):
     * 派发失败的一键稿现在也带着 `plan.result`,于是它在通用恢复路眼里等同于一份编排完成的
     * 智能稿 —— 而那条路取第一份命中的、服务端按 id 倒序 ⇒ 一份更新的失败稿会把用户
     * **真正做过的编排稿挡住**。`useCanvasBuilder` 据它在通用路里跳过失败稿,
     * 只有带 `?draftId=` 显式打开时才认。
     */
    oneclickStatus?: string | null;
    /** P2 编排异步化:plan 完成态(done|failed|running|null,从 draftMeta.plan 解;generated 未回填)。 */
    planStatus?: string | null;
    /** P2:续轮询用的 plan 作业 id。 */
    planJobId?: number | null;
    /** P2:canvas 产物(仅 getDraft 详情带),横幅点头后 JSON.parse 回填搭建器。 */
    planResultJson?: string | null;
  }
>;

// local-only: backend schema gap — POST /poster/drafts body schema absent;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface UpsertDraftRequest {
  draftId?: number | null;
  name: string;
  currentStep: number;
  formJson: string;
}

// local-only: backend schema gap — PATCH /poster/tasks/{id} body schema absent;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface UpdateTaskMetaRequest {
  name?: string;
  tags?: string[];
  isFavorite?: boolean;
  notes?: string;
}

// ===== v2.9 模版 / 重画 chip 类型 =====

/** 9 宫格位置 token,跟 ai-service templates_loader._POSITION_TOKENS 对齐。 */
// local-only: backend schema gap — passthrough enum from ai-service templates_loader, GET
// /poster/templates response. V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md.
export type AnchorPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "center-left"
  | "center"
  | "center-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

/** 哪些字段允许租户员工调整。 */
// local-only: backend schema gap — GET /poster/templates response sub-type;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export type OverridableField = "position" | "size_ratio" | "font_size";

// local-only: backend schema gap — GET /poster/templates response sub-type.
export interface LogoAnchor {
  position: AnchorPosition;
  size_ratio: number;
  padding: number;
  user_can_override: OverridableField[];
}

/** QR 跟 logo 同形态。 */
// local-only: backend schema gap — GET /poster/templates response sub-type.
export type QrcodeAnchor = LogoAnchor;

// local-only: backend schema gap — GET /poster/templates response sub-type.
export interface ContactAnchor {
  position: AnchorPosition;
  font_size: number;
  color: string;
  stroke_color: string;
  stroke_width: number;
  padding: number;
  offset_y?: number;
  user_can_override: OverridableField[];
}

// local-only: backend schema gap — GET /poster/templates response sub-type.
export interface TemplateAnchors {
  logo: LogoAnchor;
  qrcode: QrcodeAnchor;
  contact: ContactAnchor;
}

// local-only: backend schema gap — GET /poster/templates response sub-type.
export interface TemplateMeta {
  id: string;
  name: string;
  description: string;
  industry_tags: string[];
  preview_background: string;
  anchors: TemplateAnchors;
}

// local-only: backend schema gap — GET /poster/templates root response;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface TemplatesPayload {
  templates: TemplateMeta[];
}

// local-only: backend schema gap — GET /poster/instruction-presets sub-type.
export type PresetCategory = "style" | "color" | "elements" | "scene";

// local-only: backend schema gap — GET /poster/instruction-presets sub-type.
export interface InstructionPreset {
  id: string;
  label: string;
  category: PresetCategory;
  description: string;
  instruction: string;
}

// local-only: backend schema gap — GET /poster/instruction-presets root response;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface PresetsPayload {
  presets: InstructionPreset[];
}

/**
 * v2.9 wizard / detail editor 通用 overlay 配置,用户调整后落 asset.overlay_config_json。
 * 字段子集对应 TemplateAnchors,但只保留可调字段(position / size_ratio / font_size)。
 */
// local-only: drift D-12 — generated OverlayConfig has loose schema (qrcode/contact are
// Record<string,never>, position is plain string); handwritten uses stricter AnchorPosition union.
// V24 reconcile via 2026-05-17-fe-drift-poster-task-status.md proposal.
export interface OverlayConfig {
  logo?: { position?: AnchorPosition; size_ratio?: number };
  qrcode?: { position?: AnchorPosition; size_ratio?: number };
  contact?: { position?: AnchorPosition; font_size?: number };
}

/** v2.9 滤镜配置,落 asset.filter_config_json。所有字段 [-50, 50],font_size_offset [-8, 8]。 */
// 后端 filter / filterConfig 是 Map<String,Object>,字段由前端约定(见 NOTE)。
export interface FilterConfig {
  brightness?: number;
  contrast?: number;
  saturation?: number;
  warmth?: number;
  font_size_offset?: number;
}

// ─── Wave 3 (2026-05-17) 海报自由创作 Qwen 归整 ─────────────────

export type NormalizeMode = "full" | "minimal" | "titles_only";
export type AssetPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";
export type LogoAspect = "SQUARE" | "WIDE" | "TALL";

export interface NormalizeBrandAssetMeta {
  exists: boolean;
  aspect?: LogoAspect | null;
  hasTransparentBg?: boolean | null;
  colorHex?: string | null;
}

export interface NormalizeBrandAssetsMeta {
  logo?: NormalizeBrandAssetMeta;
  qrcode?: NormalizeBrandAssetMeta;
}

/**
 * 归整层 context —— 用户**已经选定的全部素材**,与 ai `normalize/schema.py:NormalizeContext` 对齐。
 *
 * ★2026-07-30 补齐:W1 那批在 `runNormalize` 里已经真发了 product / styleTokens /
 * fontStyle / ratio / extraConstraint,但这个类型一直停在 4 个字段 —— 没红是因为那些
 * 字段都用 `...(cond ? {x} : {})` 条件展开进去,**展开会绕过对象字面量的多余属性检查**。
 * 于是「前端类型」与「前端真发出去的东西」分了家,而这个类型正是给下一个人看的那份契约。
 */
export interface NormalizeContext {
  industryCode?: string;
  industryName?: string;
  brandName?: string;
  /** 用户在 StylePanel 显式选定的风格调性 — 非空时 Qwen 强制 visual_style.tone 采用 */
  userStyle?: string;
  /**
   * 2026-08-01 —— 用户的目的:**场景卡上给用户看的那句 `usage` 原话**(不是 key)。
   * 归整层据此判版面取向;没从卡片进来时整个不发(与 product/styleTokens 同口径)。
   * ★这个接口曾经与真发出去的东西分家过(条件展开绕过多余属性检查),加字段必须两边一起改。
   */
  purpose?: string;
  /** 产品库直选的快照;归整层只拿它做判断,原文由 ai 渲染层按序号逐字注入。 */
  product?: {
    code: string;
    name: string;
    category?: string;
    sellingPoints: string[];
    specs: { label: string; value: string }[];
    summary?: string;
  };
  /** L2 配色;给了色值时 ai 侧会把 `visual_style.color` 留空,由渲染层逐字写 hex。 */
  styleTokens?: {
    palette: StylePalette;
    material: string;
    lighting: string;
    tone: string;
    density: string;
  };
  /** 字体档**短语**(不是 key)—— `fontPhraseForKey` 的产物;auto 时不发。 */
  fontStyle?: string;
  ratio?: string;
  extraConstraint?: string;
  /**
   * 2026-07-30 — 品牌联系方式,只在本次要印时发(`printContact`)。
   * 归整层需要知道「有一行电话要落地」才排得对角落空间;提交侧 `brand.phone/email`
   * 与这里必须同一个闸,否则会出现「给电话留了位、指令里却没有电话」的空版面。
   */
  contact?: { phone?: string; email?: string };
}

export interface NormalizeInputRequest {
  userText: string;
  mode: NormalizeMode;
  brandAssets?: NormalizeBrandAssetsMeta;
  context?: NormalizeContext;
  /** V77 (2026-06-17) — 用户已手选比例(ratioUserTouched)时传 false,后端跳过 suggested_ratio
   *  的 LLM 推断(省一次 deepseek「推荐比例」call,且不覆盖用户已选)。省略 → 后端默认推断。 */
  needSuggestedRatio?: boolean;
}

export interface CoverageChip {
  category: string;
  label: string;
  count: number;
}

export interface RenderedSegments {
  contentDescription: string;
  systemConstraints: string;
}

export interface IntermediateLogoLayout {
  use: boolean;
  position: AssetPosition;
  size: "small" | "medium";
  needsBackplate: boolean;
}

export interface IntermediateQrLayout {
  use: boolean;
  position: AssetPosition;
  size: "small" | "medium";
  caption: string;
}

export interface IntermediateSpec {
  industry: string;
  visualScene: string;
  visualStyle: { tone: string; color: string; keywords: string[] };
  posterText: {
    brand: string;
    headline: string;
    subhead: string;
    highlights: string[];
    qualifications: string[];
    business: string[];
    slogan: string;
    contact: string;
  };
  assetLayout: { logo: IntermediateLogoLayout; qrcode: IntermediateQrLayout };
  coverageCheck: string[];
}

export interface NormalizeInputResponse {
  intermediate: IntermediateSpec;
  rendered: RenderedSegments | null;
  coverageChips: CoverageChip[];
  /**
   * 2026-05-25 — ai-service 据用户文案推断的画面比例(5 选 1 或 null)。
   * 自由创作:AI 优化后回填参数弹窗(用户未手选比例时)。
   */
  suggestedRatio?: "1:1" | "4:5" | "3:4" | "9:16" | "16:9" | null;
}
